/**
 * 브랜드 공유 링크 — 허브 주소가 아니라 우리 공개 페이지(`/share/brand/{브랜드}?…`)를 건넨다. 비회원도 열 수 있다.
 *
 * 🔑 주어는 **정규 브랜드명 하나**고, 상세 머리의 조건 중 **기간을 뺀 것**(제품군·소분류·채널·국가·페르소나)을 질의 문자열에 싣는다.
 *   `cat`/`sub`=제품군 코드 · `ch`=채널 · `co`=국가 · `pe`=페르소나 토큰(`organic`·`sponsored`·`brandAccount` · 쉼표로 잇는다).
 * 🚨 **기간은 링크에 없다** — 공유 페이지는 늘 최근 30일이다(`SHARE_BRAND_DAYS`). 주소를 고쳐 긴 기간을 여는 길이 없게
 *   기간 칸 자체를 두지 않는다(다른 키는 그냥 버려진다).
 * 🚨 **모양을 여기서 막는다**(`readShareBrandParam` · `readShareBrandQuery`) — 이 뒤는 ES 질의다. 허용 목록 밖의 값은 버린다.
 */

import { pickPersonaTokens, type BrandPersonaToken } from 'src/utils/brand/brand-persona-persist';

import { MEDIA_COUNTRIES, type MediaChannel, type MediaCountry } from 'src/sections/search-all/constants/search-all-media';

import { BRAND_HUB_DEFAULT_DAYS } from './brand-hub-period';
import { BRAND_HUB_CONTENT_CHANNELS } from '../constants/brand-hub-channels';

/** 공유 페이지 경로의 앞부분 */
export const SHARE_BRAND_ROOT = '/share/brand';

/** 브랜드 이름 길이 상한 — 넘으면 주소를 만들지도, 받지도 않는다 */
export const SHARE_BRAND_NAME_MAX = 60;

/** 공유 페이지가 세는 기간(일) — 링크와 무관하게 늘 이 값이다(허브 상세의 기본 기간) */
export const SHARE_BRAND_DAYS = BRAND_HUB_DEFAULT_DAYS;

/** 링크에 싣는 상세 머리의 조건 — 기간은 없다 */
export type ShareBrandConditions = {
  main: string;
  sub: string;
  channels: MediaChannel[];
  countries: MediaCountry[];
  personas: BrandPersonaToken[];
};

/** 아무 조건도 없을 때 */
export const SHARE_BRAND_DEFAULTS: ShareBrandConditions = { main: '', sub: '', channels: [], countries: [], personas: [] };

/**
 * @param name 정규 브랜드명
 * @param cond 상세 머리의 조건 — 기본값과 같은 칸은 싣지 않는다
 * @returns 경로(`/share/brand/…?…`) — 이름 모양이 틀리면 빈 문자열
 */
export function toBrandSharePath(name: string, cond: ShareBrandConditions = SHARE_BRAND_DEFAULTS): string {
  const brand = name.trim();
  if (!isShareBrandName(brand)) return '';
  const q = new URLSearchParams();
  if (cond.main) q.set('cat', cond.main);
  if (cond.main && cond.sub) q.set('sub', cond.sub);
  if (cond.channels.length > 0) q.set('ch', cond.channels.join(','));
  if (cond.countries.length > 0) q.set('co', cond.countries.join(','));
  if (cond.personas.length > 0) q.set('pe', cond.personas.join(','));
  const tail = q.toString();
  return `${SHARE_BRAND_ROOT}/${encodeURIComponent(brand)}${tail ? `?${tail}` : ''}`;
}

/**
 * 공유 페이지 질의 문자열 거르기 — 모르는 값은 버리고 기본값으로 선다.
 * ⚠️ 제품군 코드는 모양만 본다 — 사전에 있는지는 서버가 사전을 읽고 가린다(없으면 조건 없이).
 */
export function readShareBrandQuery(query: Record<string, string | string[] | undefined>): ShareBrandConditions {
  const one = (key: string) => {
    const v = query[key];
    return String((Array.isArray(v) ? v[0] : v) ?? '').trim();
  };
  const list = (key: string) => one(key).split(',').map((v) => v.trim()).filter(Boolean);
  const main = CATEGORY_CODE.test(one('cat')) ? one('cat') : '';
  const sub = main && CATEGORY_CODE.test(one('sub')) && one('sub').slice(0, 2) === main.slice(0, 2) ? one('sub') : '';
  return {
    main,
    sub,
    channels: [...new Set(list('ch'))].filter((c): c is MediaChannel => BRAND_HUB_CONTENT_CHANNELS.includes(c as MediaChannel)),
    countries: [...new Set(list('co').map((c) => c.toUpperCase()))].filter((c): c is MediaCountry => (MEDIA_COUNTRIES as readonly string[]).includes(c)),
    personas: pickPersonaTokens(list('pe')),
  };
}

/**
 * 공유 페이지 주소 조각 거르기.
 * @param raw 주소의 브랜드 마디(퍼센트 인코딩이든 풀린 글자든)
 * @returns 브랜드 이름 — 풀 수 없거나 모양이 틀리면 `null`
 */
export function readShareBrandParam(raw: string): string | null {
  const brand = safeDecode(raw)?.trim() ?? '';
  return isShareBrandName(brand) ? brand : null;
}

// ---- 이하 비공개 ----------------------------------------------------------

/**
 * 받는 글자 — 글자·숫자로 시작하고 글자·숫자·결합 부호·띄어쓰기와 `& ' ’ . + - _ ! · : ( )` 만.
 * ⚠️ `/`·`%`·`<>`·따옴표는 받지 않는다 — 경로를 쪼개거나 다시 풀리는 글자다.
 */
const BRAND_NAME = /^[\p{L}\p{N}][\p{L}\p{M}\p{N} &'’.+\-_!·:()]*$/u;

/** 제품군 코드 — `XX-00-ZZ` */
const CATEGORY_CODE = /^\d{2}-00-\d{2}$/;

function isShareBrandName(brand: string): boolean {
  return brand.length > 0 && brand.length <= SHARE_BRAND_NAME_MAX && BRAND_NAME.test(brand);
}

function safeDecode(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}
